import styled from 'foundations-theming/utils';
import { useState, useCallback, useEffect } from 'react';
import Raven from 'raven-js';
import { useDrop, useDrag } from 'react-dnd';
import { generatePath } from 'react-router-dom';
import PortalIdParser from 'PortalIdParser';
import { PopOutIcon } from 'foundations-assets';
import { View } from 'foundations-components/transitional/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import ACMissingContentError from 'academy-common-lib/components/error/ACMissingContentError';
import { StaticContentWidget } from 'embedded-academy-lib';
import UsageTracker from '../../../../../utils/UsageTracker';
import { useRequestLearnWhileDoing } from '../../../hooks';
import { SHORT_CONTENT_URL_FRAGMENT } from '../../../../../constants/Navigation';
import useNavigate from '../../../../../hooks/useNavigate';
import ResizableContainer from '../ResizableContainer';
import { getIsLocal } from '../../../../../utils/urls';
import { getIsIframeLoaded } from '../../../../../utils/iframeUtils';

// @bend-expect trellis-tokenized
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const LearnByDoingFloatingElement = styled.div.withConfig({
  displayName: "LearnByDoingDraggableContainer__LearnByDoingFloatingElement",
  componentId: "xloqqd-0"
})(({
  isDragging,
  left,
  top,
  theme
}) => ({
  backgroundColor: theme.color['specialty-scrim'],
  borderRadius: '10px',
  cursor: 'move',
  left,
  position: 'absolute',
  pointerEvents: isDragging ? 'none' : 'auto',
  top,
  zIndex: 99
}));

/**
 * Draggable container for react Dnd Floating element, which stays under the iframe
 * @bend-expect trellis-tokenized
 */
const DroppableArea = styled.div.withConfig({
  displayName: "LearnByDoingDraggableContainer__DroppableArea",
  componentId: "xloqqd-1"
})(["height:100%;width:100%;background-color:transparent;position:absolute;z-index:1;"]);
const LearnByDoingFloatingElementType = {
  FloatingElement: 'floating-element'
};
const LearnByDoingDraggableContainer = ({
  children,
  otgId,
  creatorPortalId,
  creatorHublet,
  iFrameId
}) => {
  const navigate = useNavigate();
  const [isContentLoaded, setIsContentLoaded] = useState(false);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const [isIframeError, setIsIframeError] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const {
    handleVideoPlayed,
    handleVideoFinished
  } = useRequestLearnWhileDoing();
  const [floatingElement, setFloatingElement] = useState({
    top: window.innerHeight / 1.8,
    left: window.innerWidth / 1.4
  });
  const moveFloatingElement = useCallback((left, top) => {
    setFloatingElement({
      left,
      top
    });
  }, [setFloatingElement]);
  const [{
    isDragging
  }, drag] = useDrag(() => ({
    // Drag's type should be the same as the drop's accept
    type: LearnByDoingFloatingElementType.FloatingElement,
    item: {
      id: LearnByDoingFloatingElementType.FloatingElement,
      left: floatingElement.left,
      top: floatingElement.top
    },
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  }), [floatingElement.left, floatingElement.top]);
  const [, drop] = useDrop(() => ({
    accept: LearnByDoingFloatingElementType.FloatingElement,
    drop(item, monitor) {
      const delta = monitor.getDifferenceFromInitialOffset();
      const left = Math.round(item.left + delta.x);
      const top = Math.round(item.top + delta.y);
      moveFloatingElement(left, top);
      return undefined;
    }
  }), [moveFloatingElement]);
  useEffect(() => {
    const iframeElement = document.getElementById(iFrameId);
    const droppableArea = document.querySelector('[data-test-id="learn-by-doing-droppable-area"]');

    //React Dnd Floating element cannot be used in the iframe, so we need to handle the zIndex of draggable area and iframe manually
    //This is a workaround to allow the floating element to be used in the iframe
    if (iframeElement && droppableArea) {
      iframeElement.onload = () => {
        if (getIsLocal()) {
          setIsIframeLoaded(true);
          return;
        }
        const loaded = getIsIframeLoaded(iframeElement);
        if (!loaded) {
          Raven.captureException(new Error('Software Discovery iframe failed to load'), {
            extra: {
              otgId,
              creatorPortalId,
              creatorHublet,
              portalId: PortalIdParser.getPortalIdFromPath()
            }
          });
          setIsIframeError(true);
        } else {
          setIsIframeLoaded(true);
        }
      };
      const droppableAreaStyle = window.getComputedStyle(droppableArea);
      iframeElement.style.zIndex = isDragging || isResizing ? (Number(droppableAreaStyle.zIndex) - 1).toString() : (Number(droppableAreaStyle.zIndex) + 1).toString();
    }
  }, [creatorHublet, creatorPortalId, isDragging, isResizing, otgId, iFrameId]);
  const isLoaded = isContentLoaded && isIframeLoaded;
  const handleContentLoaded = () => {
    setIsContentLoaded(true);
  };
  const setHandleVideoFinished = () => {
    handleVideoFinished(true);
  };
  const setHandleVideoPlayed = () => {
    handleVideoPlayed(true);
  };
  const handleCloseLearnWhileDoing = () => {
    const otgPath = generatePath(SHORT_CONTENT_URL_FRAGMENT, {
      shortId: otgId
    });
    UsageTracker.track('contentInteraction', {
      action: 'learn-by-doing-close-draggable-video-element-clicked',
      shortId: String(otgId)
    });
    navigate(otgPath);
  };
  const handleIsResizing = resizing => {
    setIsResizing(resizing);
  };
  return /*#__PURE__*/_jsx(_Fragment, {
    children: isIframeError ? /*#__PURE__*/_jsx(ACMissingContentError, {
      to: `/shortvideo/${otgId}/`
    }) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [!isLoaded && /*#__PURE__*/_jsx(LoadingOverlay, {
        layout: "centered",
        use: "dots"
      }), children, /*#__PURE__*/_jsx(DroppableArea, {
        "data-test-id": "learn-by-doing-droppable-area",
        ref: drop
      }), /*#__PURE__*/_jsxs(LearnByDoingFloatingElement, {
        "data-test-id": "learn-by-doing-floating-element",
        isDragging: isDragging,
        left: floatingElement.left,
        ref: drag,
        top: floatingElement.top,
        children: [/*#__PURE__*/_jsx(Flex, {
          inlineSize: "100%",
          justifyContent: "end",
          paddingInline: "100",
          children: /*#__PURE__*/_jsx(IconButton, {
            name: "remove",
            onClick: handleCloseLearnWhileDoing,
            size: "sm",
            use: "transparent",
            children: /*#__PURE__*/_jsx(PopOutIcon, {
              color: "icon-core-on-fill-default"
            })
          })
        }), /*#__PURE__*/_jsx(ResizableContainer, {
          onResizing: handleIsResizing,
          children: /*#__PURE__*/_jsx(View, {
            pointerEvents: isDragging || isResizing ? 'none' : 'auto',
            children: /*#__PURE__*/_jsx(StaticContentWidget, {
              authType: "app",
              contentId: otgId,
              enablePictureInPicture: false,
              hublet: creatorHublet,
              name: "LearnByDoingVideo",
              onContent: handleContentLoaded,
              onVideoFinish: setHandleVideoFinished,
              onVideoPlay: setHandleVideoPlayed,
              portalId: creatorPortalId === null || creatorPortalId === void 0 ? void 0 : creatorPortalId.toString(),
              renderContext: "embedded-learnByDoing",
              shortId: otgId,
              variant: "video"
            })
          })
        })]
      })]
    })
  });
};
export default LearnByDoingDraggableContainer;